import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';
import type { MarketSymbol } from './useMarketSymbols';

/**
 * 為替・株式分析ページのデータ層フック。
 *
 * 選択中の銘柄と期間に対して、日足 + テクニカル指標 + サマリー (get_market_chart) と
 * ARIMA 予測 (get_market_forecast) を並列取得する。予測の失敗はチャート表示を妨げず
 * warning として保持する。
 */

export type MarketRange = '3m' | '6m' | '1y' | '3y' | '5y' | 'max';

export const MARKET_RANGES: { key: MarketRange; label: string }[] = [
  { key: '3m', label: '3M' },
  { key: '6m', label: '6M' },
  { key: '1y', label: '1Y' },
  { key: '3y', label: '3Y' },
  { key: '5y', label: '5Y' },
  { key: 'max', label: 'MAX' },
];

export type MarketPriceRow = {
  trade_date: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  adj_close: number | null;
  volume: number | null;
  sma_25: number | null;
  sma_75: number | null;
  sma_200: number | null;
  ema_12: number | null;
  ema_26: number | null;
  bb_mid: number | null;
  bb_upper: number | null;
  bb_lower: number | null;
  rsi_14: number | null;
  change: number | null;
  change_pct: number | null;
};

export type MarketSummary = {
  as_of: string;
  close: number | null;
  change: number | null;
  change_pct: number | null;
  high_52w: number | null;
  low_52w: number | null;
  rsi_14: number | null;
  sma_25: number | null;
  sma_75: number | null;
  rows_total: number;
};

export type MarketChart = {
  symbol: Omit<MarketSymbol, 'coverage'>;
  range: MarketRange;
  rows: MarketPriceRow[];
  summary: MarketSummary | null;
};

export type MarketForecastRow = {
  trade_date: string;
  forecast: number | null;
  lower: number | null;
  upper: number | null;
};

export type MarketForecast = {
  symbol_code: string;
  rows: MarketForecastRow[];
  method: string;
  alpha: number;
  lookback: number;
  warning: string | null;
  last_actual: { trade_date: string; close: number | null } | null;
};

const CHART_URL = ep('get_market_chart');
const FORECAST_URL = ep('get_market_forecast');
const SYNC_URL = ep('run_market_sync');

export const DEFAULT_SYMBOL = 'JPY=X';
export const FORECAST_HORIZON = 30;

export function useMarketAnalysis(initialSymbol: string = DEFAULT_SYMBOL) {
  const [symbolCode, setSymbolCode] = useState<string>(initialSymbol);
  const [range, setRange] = useState<MarketRange>('1y');
  const [chart, setChart] = useState<MarketChart | null>(null);
  const [forecast, setForecast] = useState<MarketForecast | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!symbolCode) return;
    setLoading(true);
    setError(null);
    try {
      const [chartRes, forecastRes] = await Promise.all([
        axios.post(CHART_URL, { symbol_code: symbolCode, range }),
        axios.post(FORECAST_URL, { symbol_code: symbolCode, horizon: FORECAST_HORIZON }).catch((e) => e?.response ?? null),
      ]);
      if (chartRes.data?.status === 'success') {
        setChart(chartRes.data.data as MarketChart);
      } else {
        setChart(null);
        setError(chartRes.data?.error || 'チャートデータの取得に失敗しました');
      }
      if (forecastRes?.data?.status === 'success') {
        setForecast(forecastRes.data.data as MarketForecast);
      } else {
        // 予測はチャートの補助情報なので、失敗しても warning 扱いにとどめる
        setForecast({
          symbol_code: symbolCode, rows: [], method: '-', alpha: 0.2, lookback: 0,
          warning: forecastRes?.data?.error || '予測の取得に失敗しました', last_actual: null,
        });
      }
    } catch (e: any) {
      setChart(null);
      setError(e?.response?.data?.error || e?.message || 'チャートデータの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [symbolCode, range]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /** 選択中の銘柄について最新の日足を取り直してから再読込する。 */
  const runSync = useCallback(async (period: string = '1mo') => {
    setSyncing(true);
    try {
      const res = await axios.post(SYNC_URL, { symbol_code: symbolCode, period });
      if (res.data?.status !== 'success') {
        throw new Error(res.data?.error || '最新データの取得に失敗しました');
      }
      const rows = Number(res.data.rows || 0);
      await fetchAll();
      return rows;
    } finally {
      setSyncing(false);
    }
  }, [symbolCode, fetchAll]);

  return {
    symbolCode, setSymbolCode,
    range, setRange,
    chart, forecast,
    loading, syncing, error,
    refresh: fetchAll,
    runSync,
  };
}

export type UseMarketAnalysisResult = ReturnType<typeof useMarketAnalysis>;
